import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserOrganization } from "@/hooks/use-user-organization";
import type { EnhancedCreditPackage } from "@/hooks/use-enhanced-credit-packages";

export interface ReservedPackagesByYear {
  yearNumber: number;
  coverageYear?: number;
  releaseDate?: string;
  packages: EnhancedCreditPackage[];
  totalCredits: number;
  deviceCount: number;
}

export const useReservedCreditPackages = (orderId?: string) => {
  const { data: organizationId } = useUserOrganization();

  return useQuery({
    queryKey: ['reserved-credit-packages', orderId, organizationId],
    queryFn: async () => {
      let query = supabase
        .from('device_credit_packages')
        .select('*')
        .eq('is_released', false)
        .order('year_number', { ascending: true });

      if (orderId) {
        query = query.eq('order_id', orderId);
      }

      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      } else {
        return [];
      }

      const { data, error } = await query;
      if (error) throw error;

      // Group packages by year_number
      const packagesByYear = (data || []).reduce((acc, pkg) => {
        const yearNum = pkg.year_number || 1;
        if (!acc[yearNum]) {
          acc[yearNum] = {
            yearNumber: yearNum,
            coverageYear: pkg.coverage_year,
            releaseDate: pkg.release_date,
            packages: [],
            totalCredits: 0,
            deviceCount: 0
          };
        }
        acc[yearNum].packages.push(pkg as EnhancedCreditPackage);
        acc[yearNum].totalCredits += pkg.total_credits || 0;
        acc[yearNum].deviceCount += 1;
        return acc;
      }, {} as Record<number, ReservedPackagesByYear>);

      return Object.values(packagesByYear).sort((a, b) => a.yearNumber - b.yearNumber);
    },
    enabled: !!organizationId,
  });
};

export const useOrderYearBreakdown = (orderId: string) => {
  const { data: organizationId } = useUserOrganization();

  return useQuery({
    queryKey: ['order-year-breakdown', orderId, organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('device_credit_packages')
        .select('*')
        .eq('order_id', orderId)
        .order('year_number', { ascending: true });

      if (error) throw error;

      // Group by year
      const yearBreakdown = (data || []).reduce((acc, pkg) => {
        const yearNum = pkg.year_number || 1;
        if (!acc[yearNum]) {
          acc[yearNum] = {
            yearNumber: yearNum,
            coverageYear: pkg.coverage_year,
            releaseDate: pkg.release_date,
            isReleased: pkg.is_released,
            totalCredits: 0,
            deviceCount: 0,
            packages: []
          };
        }
        acc[yearNum].totalCredits += pkg.total_credits || 0;
        acc[yearNum].deviceCount += 1;
        acc[yearNum].packages.push(pkg);
        // If any package in the year is released, mark the year as released
        if (pkg.is_released) {
          acc[yearNum].isReleased = true;
        }
        return acc;
      }, {} as Record<number, {
        yearNumber: number;
        coverageYear?: number;
        releaseDate?: string;
        isReleased: boolean;
        totalCredits: number;
        deviceCount: number;
        packages: any[];
      }>);

      return Object.values(yearBreakdown).sort((a, b) => a.yearNumber - b.yearNumber);
    },
    enabled: !!orderId,
  });
};
